import React, { useEffect, useState } from "react";
import { useQuery } from "urql";
import { Link, useSearchParams } from "wouter";
import { useQueryState } from "../../../components/src/use-query-state";

import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateForm } from "../../../components/src/dateForm";

import { INSTRUMENT_POSITIONS_DATA_QUERY } from "../../../graphql/src/position/queries";
import { TableGrouper } from "../../../components/src/react-table/TableGrouper";
import { firstChildAggregation } from "../../../components/src/react-table/features";
import { Position } from "../types.generated";

interface AccountData {
    total: string;
    instrumentId: string;
    clientId: string;
    clientName: string;
    name: string;
    accountId: string;
    accountName: string;
    quantity: number;
}
interface ExternalAccountData {
    total: string;
    instrumentId: string;
    clientId: string;
    clientName: string;
    name: string;
    externalAccountId: string;
    externalAccountName: string;
    quantity: number;
}

export const InstrumentPositionsPage = (): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const [accountData, setAccountData] = useState<AccountData[]>([]);
    const [externalAccountData, setExternalAccountData] = useState<ExternalAccountData[]>([]);

    const evaluationDate = new Date().toISOString().substring(0, 10);
    const [date] = useQueryState("date", evaluationDate);

    const [{ data, fetching, error }] = useQuery<{
        accountPositions?: Partial<Position>[];
        externalAccountPositions?: Partial<Position>[];
    }>({
        query: INSTRUMENT_POSITIONS_DATA_QUERY,
        variables: { instrumentIds: queryArgs.get("instrumentIds") as string, filterZeroPositions: true, date },
        requestPolicy: "cache-and-network",
        pause: !queryArgs.get("instrumentIds") || !date
    });

    const columns = React.useMemo(
        () => [
            {
                header: "Positions",
                columns: [
                    {
                        header: "Total",
                        accessorKey: "total"
                    },
                    {
                        header: "ClientId",
                        accessorKey: "clientId",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Client",
                        accessorKey: "clientName",
                        aggregationFn: firstChildAggregation,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            return (
                                <a href={`/parties/${row.original.clientId}`} target="_blank" rel="noopener noreferrer">
                                    {cellProps.getValue()}
                                </a>
                            );
                        }
                    },
                    {
                        header: "Account",
                        accessorKey: "accountName",
                        aggregationFn: firstChildAggregation,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            return (
                                <a href={`/parties/${row.original.clientId}/accounts/${row.original.accountId}`} target="_blank" rel="noopener noreferrer">
                                    {cellProps.getValue()}
                                </a>
                            );
                        }
                    },
                    {
                        header: "AccountId",
                        accessorKey: "accountId",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "ExternalAccount",
                        accessorKey: "externalAccountName",
                        aggregationFn: firstChildAggregation,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            return (
                                <Link
                                    to={`/parties/${row.original.clientId}/externalAccounts/${row.original.externalAccountId}`}
                                    target={"_blank"}
                                >
                                    {cellProps.getValue()}
                                </Link>
                            );
                        }
                    },
                    {
                        header: "ExternalAccountId",
                        accessorKey: "externalAccountId",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "InstrumentId",
                        accessorKey: "instrumentId",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Name",
                        accessorKey: "name",
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            return (
                                <a href={`/instruments/${row.original.instrumentId}`} target="_blank" rel="noopener noreferrer">
                                    {cellProps.getValue()}
                                </a>
                            );
                        }
                    },
                    {
                        header: "Quantity",
                        accessorKey: "quantity",
                        cell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                        aggregatedCell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                        aggregationFn: "sum"
                    }
                ]
            }
        ],
        []
    );

    useEffect(() => {
        if (data) {
            const accountData_: AccountData[] = [];
            const externalAccountData_: ExternalAccountData[] = [];

            for (const accountPosition of data.accountPositions) {
                accountData_.push({
                    total: "Total",
                    clientId: accountPosition.clientId,
                    clientName: accountPosition.client.name,
                    instrumentId: accountPosition.instrumentId,
                    name: accountPosition.instrument.name,
                    accountId: accountPosition.accountId,
                    accountName: accountPosition.account.name,
                    quantity: accountPosition.quantity
                });
            }
            for (const externalAccountPosition of data.externalAccountPositions) {
                externalAccountData_.push({
                    total: "Total",
                    clientId: externalAccountPosition.clientId,
                    clientName: externalAccountPosition.client.name,
                    instrumentId: externalAccountPosition.instrumentId,
                    name: externalAccountPosition.instrument.name,
                    externalAccountId: externalAccountPosition.externalAccountId,
                    externalAccountName: externalAccountPosition.externalAccount.name,
                    quantity: externalAccountPosition.quantity
                });
            }

            setAccountData(accountData_);
            setExternalAccountData(externalAccountData_);
        }
    }, [data]);

    const accountHiddenColumns = ["clientId", "accountId", "externalAccountId", "instrumentId", "externalAccountName"];
    const externalAccountHiddenColumns = ["clientId", "accountId", "externalAccountId", "instrumentId", "accountName"];
    const accountGroupBy = ["total", "clientName", "accountName"];
    const externalAccountGroupBy = ["total", "clientName", "externalAccountName"];
    const expandedPositions = {};

    if (fetching)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (!queryArgs.get("instrumentIds")) return <div>No instrument ids specified!</div>;

    return (
        <div className="mx-auto px-4">
            <div>
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full sm:w-3/12 lg:w-2/12 px-2">
                        <DateForm defaultDateString={date} dateName={"date"}></DateForm>
                    </div>
                </div>
                <div className="inline-flex flex-wrap mt-2">
                    <div className="mr-3">
                        <h4>Account Positions</h4>
                        <TableGrouper
                            columns={columns}
                            data={accountData}
                            expanded={expandedPositions}
                            groupBy={accountGroupBy}
                            hiddenColumns={accountHiddenColumns}
                            compactMode={true}
                            captionColumn="name"
                        />
                    </div>
                </div>
                <div className="inline-flex flex-wrap mt-2">
                    <div className="mr-3">
                        <h4>Custody Account Positions</h4>
                        <TableGrouper
                            columns={columns}
                            data={externalAccountData}
                            expanded={expandedPositions}
                            groupBy={externalAccountGroupBy}
                            hiddenColumns={externalAccountHiddenColumns}
                            compactMode={true}
                            captionColumn="name"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
